import { Row, Wrapper } from "../Grid";
import classNames from "classnames";
import React, { useRef, useState } from "react";
import { useClickAway } from "react-use";
import { ToolbarChildItem } from "../../model/menu";
import dropdownArrow from "./dropdown-arrow.svg";
import NavLink from "./NavLink";
import styles from "./SecondaryNav.module.scss";
import Text from "../Text/Text";
import { toolbarNavUnderlineLinkClass } from "../Toolbar/ToolBarNav/ToolBarNav";

interface SecondaryNavProps {
  items: ToolbarChildItem[];
  leafMatch?: ToolbarChildItem;
}

const SecondaryNav = ({ items, leafMatch }: SecondaryNavProps) => {
  return (
    <div className={styles.root}>
      <Wrapper>
        <Row align="center" justify="center" className={styles.secondaryNavRow}>
          <ul
            className={styles.secondaryNavContainer}
            id="secondary-menu"
            role="menu"
          >
            {(items || []).map(secondaryLink => {
              if (
                secondaryLink.childLinks &&
                secondaryLink.childLinks.length > 0
              ) {
                return (
                  <TertiaryNav
                    key={secondaryLink._key}
                    secondaryLink={secondaryLink}
                    leafMatch={leafMatch}
                  />
                );
              }
              return (
                <li key={secondaryLink._key} role="presentation">
                  <NavLink
                    activeClassName={classNames(
                      toolbarNavUnderlineLinkClass,
                      styles.bold
                    )}
                    data={secondaryLink.internalLink}
                    className={classNames(
                      styles.secondaryNavLink,
                      leafMatch &&
                        leafMatch._key === secondaryLink._key &&
                        classNames(styles.activeChildLink, styles.bold)
                    )}
                    role="menuitem"
                  >
                    <Text variant="toolbarDefault">{secondaryLink.title}</Text>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </Row>
      </Wrapper>
    </div>
  );
};

interface TertiaryNavProps {
  secondaryLink: ToolbarChildItem;
  leafMatch?: ToolbarChildItem;
}

const TertiaryNav = ({ secondaryLink, leafMatch }: TertiaryNavProps) => {
  const [showTertiaryNav, setShowTertiaryNav] = useState(false);
  const menuRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useClickAway(menuRef, e => {
    if (
      buttonRef.current &&
      e.target instanceof Node &&
      buttonRef.current.contains(e.target)
    ) {
      // Clicks on the button should only be handled by handleClick.
      return;
    }
    setShowTertiaryNav(false);
  });
  const titleAsId = secondaryLink.title?.toString().replace(/\s/g, "");
  return (
    <li
      className={styles.tertiaryLinkContainer}
      onMouseLeave={() => setShowTertiaryNav(false)}
      role="presentation"
    >
      <button
        id={`${titleAsId}-menubutton`}
        aria-haspopup="true"
        aria-controls={`${titleAsId}-menu`}
        className={classNames(
          styles.secondaryNavLink,
          leafMatch &&
            leafMatch._key === secondaryLink._key &&
            classNames(styles.activeChildLink, styles.bold)
        )}
        onClick={() => setShowTertiaryNav(prev => !prev)}
        onMouseEnter={() => setShowTertiaryNav(true)}
        ref={buttonRef}
        role="menuitem"
      >
        <Text variant="toolbarDefault">{secondaryLink.title}</Text>
        <img className={styles.icon} src={dropdownArrow} alt="" />
      </button>
      {showTertiaryNav && (
        <ul
          id={`${titleAsId}-menu`}
          role="menu"
          aria-labelledby={`${titleAsId}-menubutton`}
          className={styles.tertiaryLinkOptions}
          ref={menuRef}
        >
          {secondaryLink.childLinks!.map(tertiaryLink => (
            <li key={tertiaryLink._key} role="presentation">
              <NavLink
                activeClassName={styles.bold}
                data={tertiaryLink.internalLink}
                className={styles.tertiaryLink}
                role="menuitem"
              >
                <Text data-content={tertiaryLink.title} variant="default">
                  {tertiaryLink.title}
                </Text>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export default SecondaryNav;
